import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import { listConfigTemplates, listConfigTemplateTags } from '../../services/configTemplateService';
import { listStates } from '../../services/productMasterService';
import type { CpmState } from '../../types/centralProductMaster.types';
import type {
    ConfigTemplateActiveStatus,
    ConfigTemplateLevel,
    ConfigTemplateLicenceType,
    ConfigTemplateListItem,
    ConfigTemplateListRequest,
} from '../../types/configTemplate.types';

export interface ConfigTemplatePlaceFilters {
    search: string;
    states: string[];
    tags: string[];
    licenceTypes: ConfigTemplateLicenceType[];
    activeStatus: ConfigTemplateActiveStatus[];
    sort: 'name' | '-name';
}

export interface ConfigTemplatePlaceState {
    templates: ConfigTemplateListItem[];
    total: number;
    page: number;
    status: 'idle' | 'loading' | 'success' | 'error';
    persistError: boolean;
    loadingMore: boolean;
    moreError: boolean;
    error: string | null;
    filters: ConfigTemplatePlaceFilters;
}

const emptyPlace = (): ConfigTemplatePlaceState => ({
    templates: [],
    total: 0,
    page: 1,
    status: 'idle',
    persistError: false,
    loadingMore: false,
    moreError: false,
    error: null,
    filters: {
        search: '',
        states: [],
        tags: [],
        licenceTypes: [],
        activeStatus: [],
        sort: 'name',
    },
});

interface ConfigTemplatesListState {
    STORE_BRAND: ConfigTemplatePlaceState;
    STORE: ConfigTemplatePlaceState;
    states: CpmState[];
    statesStatus: 'idle' | 'loading' | 'success' | 'error';
    tags: Record<ConfigTemplateLevel, string[]>;
    tagsStatus: Record<ConfigTemplateLevel, 'idle' | 'loading' | 'success' | 'error'>;
}

const initialState: ConfigTemplatesListState = {
    STORE_BRAND: emptyPlace(),
    STORE: emptyPlace(),
    states: [],
    statesStatus: 'idle',
    tags: { STORE_BRAND: [], STORE: [] },
    tagsStatus: { STORE_BRAND: 'idle', STORE: 'idle' },
};

function readSettingsCount(row: {
    settingsCount?: unknown;
    inLeafCount?: unknown;
}): number {
    const raw = row.settingsCount ?? row.inLeafCount;
    const count = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(count) ? count : 0;
}

function asListItem(row: ConfigTemplateListItem): ConfigTemplateListItem {
    return {
        ...row,
        settingsCount: readSettingsCount(row),
    };
}

function rejectMessage(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'message' in err) {
        const message = (err as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
}

export const fetchConfigTemplatePlace = createAsyncThunk(
    'configTemplatesList/fetchPlace',
    async (
        args: { level: ConfigTemplateLevel; append?: boolean },
        { getState, rejectWithValue }
    ) => {
        const state = (getState() as RootState).configTemplatesList[args.level];
        const page = args.append ? state.page + 1 : 1;
        const body: ConfigTemplateListRequest = {
            level: args.level,
            page,
            limit: 100,
            search: state.filters.search.trim() || undefined,
            states: state.filters.states.length ? state.filters.states : undefined,
            tags: state.filters.tags.length ? state.filters.tags : undefined,
            licenceTypes: state.filters.licenceTypes.length
                ? state.filters.licenceTypes
                : undefined,
            activeStatus: state.filters.activeStatus.length
                ? state.filters.activeStatus
                : undefined,
            sort: state.filters.sort,
        };
        try {
            const data = await listConfigTemplates(body);
            return { level: args.level, append: Boolean(args.append), page, data };
        } catch (err: unknown) {
            return rejectWithValue({
                level: args.level,
                append: Boolean(args.append),
                message: rejectMessage(err, 'Could not load these templates.'),
            });
        }
    }
);

export const fetchConfigTemplateStates = createAsyncThunk(
    'configTemplatesList/fetchStates',
    async (_, { rejectWithValue }) => {
        try {
            return await listStates();
        } catch (err: unknown) {
            return rejectWithValue(rejectMessage(err, 'Could not load these options.'));
        }
    }
);

export const fetchConfigTemplateTags = createAsyncThunk(
    'configTemplatesList/fetchTags',
    async (args: { level: ConfigTemplateLevel; q?: string }, { rejectWithValue }) => {
        try {
            const tags = await listConfigTemplateTags(args);
            return { level: args.level, tags };
        } catch (err: unknown) {
            return rejectWithValue({
                level: args.level,
                message: rejectMessage(err, 'Could not load these options.'),
            });
        }
    }
);

const configTemplatesListSlice = createSlice({
    name: 'configTemplatesList',
    initialState,
    reducers: {
        setPlaceFilters(
            state,
            action: PayloadAction<{
                level: ConfigTemplateLevel;
                filters: Partial<ConfigTemplatePlaceFilters>;
            }>
        ) {
            const place = state[action.payload.level];
            place.filters = { ...place.filters, ...action.payload.filters };
        },
        upsertPlaceRow(
            state,
            action: PayloadAction<{ level: ConfigTemplateLevel; row: ConfigTemplateListItem }>
        ) {
            const place = state[action.payload.level];
            const idx = place.templates.findIndex((row) => row._id === action.payload.row._id);
            if (idx >= 0) {
                const previous = place.templates[idx];
                const next = asListItem(action.payload.row);
                place.templates[idx] = {
                    ...previous,
                    ...next,
                    settingsCount:
                        action.payload.row.settingsCount != null
                            ? readSettingsCount(action.payload.row)
                            : previous.settingsCount,
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConfigTemplatePlace.pending, (state, action) => {
                const place = state[action.meta.arg.level];
                if (action.meta.arg.append) {
                    place.persistError = false;
                    place.loadingMore = true;
                    place.moreError = false;
                    return;
                }
                if (place.status === 'success' && place.templates.length > 0) {
                    place.persistError = false;
                    return;
                }
                place.status = 'loading';
                place.error = null;
            })
            .addCase(fetchConfigTemplatePlace.fulfilled, (state, action) => {
                const place = state[action.payload.level];
                place.status = 'success';
                place.error = null;
                place.persistError = false;
                place.loadingMore = false;
                place.moreError = false;
                place.page = action.payload.page;
                place.total = action.payload.data.total;
                const incoming = (action.payload.data.templates ?? []).map(asListItem);
                place.templates = action.payload.append
                    ? [...place.templates, ...incoming]
                    : incoming;
            })
            .addCase(fetchConfigTemplatePlace.rejected, (state, action) => {
                const payload = action.payload as
                    | { level: ConfigTemplateLevel; append: boolean; message: string }
                    | undefined;
                const level = payload?.level ?? action.meta.arg.level;
                const place = state[level];
                if (payload?.append) {
                    place.loadingMore = false;
                    place.moreError = true;
                    return;
                }
                if (place.templates.length > 0) {
                    place.persistError = true;
                    place.error = payload?.message ?? 'Could not load these templates.';
                    return;
                }
                place.status = 'error';
                place.error = payload?.message ?? 'Could not load these templates.';
            })
            .addCase(fetchConfigTemplateStates.pending, (state) => {
                state.statesStatus = 'loading';
            })
            .addCase(fetchConfigTemplateStates.fulfilled, (state, action) => {
                state.statesStatus = 'success';
                const rows = Array.isArray(action.payload) ? action.payload : [];
                state.states = [...rows].sort((a, b) =>
                    (a.name ?? a.state ?? '').localeCompare(b.name ?? b.state ?? '')
                );
            })
            .addCase(fetchConfigTemplateStates.rejected, (state) => {
                state.statesStatus = 'error';
            })
            .addCase(fetchConfigTemplateTags.pending, (state, action) => {
                state.tagsStatus[action.meta.arg.level] = 'loading';
            })
            .addCase(fetchConfigTemplateTags.fulfilled, (state, action) => {
                state.tagsStatus[action.payload.level] = 'success';
                state.tags[action.payload.level] = action.payload.tags;
            })
            .addCase(fetchConfigTemplateTags.rejected, (state, action) => {
                const payload = action.payload as { level: ConfigTemplateLevel } | undefined;
                const level = payload?.level ?? action.meta.arg.level;
                state.tagsStatus[level] = 'error';
            });
    },
});

export const { setPlaceFilters, upsertPlaceRow } = configTemplatesListSlice.actions;

export const useConfigTemplatePlace = (level: ConfigTemplateLevel) =>
    useSelector((state: RootState) => state.configTemplatesList[level]);

export const useConfigTemplateStates = () =>
    useSelector((state: RootState) => ({
        states: state.configTemplatesList.states,
        status: state.configTemplatesList.statesStatus,
    }));

export const useConfigTemplateTags = (level: ConfigTemplateLevel) =>
    useSelector((state: RootState) => ({
        tags: state.configTemplatesList.tags[level],
        status: state.configTemplatesList.tagsStatus[level],
    }));

export default configTemplatesListSlice.reducer;
